import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAppConfig } from "../lib/config";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PaymentStatus() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const { payments_enabled } = useAppConfig();
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const attempts = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Payments toggled off — bounce back to the dashboard with a soft notice.
    if (!payments_enabled) {
      setStatus("disabled");
      return;
    }
    if (!session_id) { setStatus("error"); return; }

    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${session_id}`);
        setData(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts.current >= 10) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setStatus("error");
      }
    };
    poll();
    return () => { stopped = true; };
  }, [session_id, payments_enabled]);

  return (
    <Layout>
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        {status === "disabled" && (
          <>
            <h1 className="font-heading text-3xl text-navy mb-2" data-testid="payment-disabled">
              Payments are paused
            </h1>
            <p className="text-neutral-500 mb-8">
              inAirlie is currently free for everyone. Sponsorships will return soon — we&apos;ll let you know.
            </p>
            <Link to="/dashboard">
              <Button className="rounded-full bg-navy hover:bg-navy/90">Back to dashboard</Button>
            </Link>
          </>
        )}
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-navy animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-3xl text-navy mb-2" data-testid="payment-checking">Processing your payment...</h1>
            <p className="text-neutral-500">Hang tight, this usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h1 className="font-heading text-3xl text-navy mb-2" data-testid="payment-success">Payment successful!</h1>
            <p className="text-neutral-500 mb-2">Your venue is now sponsored and will appear at the top of search results.</p>
            {data && <p className="text-sm text-neutral-400 mb-8">Amount: ${data.amount?.toFixed(2)} {data.currency?.toUpperCase()}</p>}
            <Button onClick={() => navigate("/dashboard")} className="rounded-full bg-navy hover:bg-navy/90" data-testid="back-to-dashboard">
              Back to dashboard
            </Button>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <XCircle className="w-16 h-16 text-rose-500 mx-auto mb-4" />
            <h1 className="font-heading text-3xl text-navy mb-2" data-testid="payment-error">
              {status === "timeout" ? "Still processing..." : "Payment incomplete"}
            </h1>
            <p className="text-neutral-500 mb-8">
              {status === "timeout" ? "We'll confirm via email once done." : "No charge was made. Please try again."}
            </p>
            <Link to="/dashboard">
              <Button variant="outline" className="rounded-full">Back to dashboard</Button>
            </Link>
          </>
        )}
      </div>
    </Layout>
  );
}
